"use client";

import { publicPath } from "@/lib/public-path";
import { MediaImage as Image } from "@/components/media-image";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { DiscAlbum } from "lucide-react";
import { AsyncBoundary, Skeleton } from "@gerama/ui";
import { browserCoreApi } from "@/lib/api";

/** An artist's albums + playlists — the "discography" the product plan asks the artist page to carry. */
export function ArtistDiscography({ artistId }: { artistId: number }) {
  const albumsQuery = useQuery({
    queryKey: ["albums", "list", "artist", artistId],
    queryFn: ({ signal }) => browserCoreApi.albums.list({ artistId, limit: 20 }, signal),
  });
  const playlistsQuery = useQuery({
    queryKey: ["playlists", "list", "artist", artistId],
    queryFn: ({ signal }) => browserCoreApi.playlists.list({ artistId, limit: 20 }, signal),
  });

  return (
    <div className="space-y-8">
      <AsyncBoundary
        variant="immersive"
        query={albumsQuery}
        skeleton={<Skeleton className="h-40 w-full rounded-xl" />}
        isEmpty={(res) => res.list.length === 0}
        empty={<></>}
        render={(res) => (
          <section>
            <h2 className="mb-3 text-lg font-semibold">آلبوم‌ها</h2>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {res.list.map((album) => (
                <Link key={album.id} href={publicPath("albums", album)} className="w-32 shrink-0 sm:w-40">
                  <div className="bg-muted relative aspect-square w-full overflow-hidden rounded-lg">
                    {album.cover ? (
                      <Image
                        src={album.cover}
                        alt={album.name}
                        fill
                        className="object-cover"
                        sizes="160px"
                      />
                    ) : (
                      <DiscAlbum className="text-muted-foreground absolute inset-0 m-auto size-8" />
                    )}
                  </div>
                  <p className="mt-2 truncate text-sm font-medium">{album.name}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      />

      <AsyncBoundary
        variant="immersive"
        query={playlistsQuery}
        skeleton={<Skeleton className="h-40 w-full rounded-xl" />}
        isEmpty={(res) => res.list.length === 0}
        empty={<></>}
        render={(res) => (
          <section>
            <h2 className="mb-3 text-lg font-semibold">پلی‌لیست‌ها</h2>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {res.list.map((playlist) => (
                <Link
                  key={playlist.id}
                  href={`/playlists/${playlist.id}`}
                  className="w-32 shrink-0 sm:w-40"
                >
                  <div className="bg-muted relative aspect-square w-full overflow-hidden rounded-lg">
                    {playlist.cover ? (
                      <Image
                        src={playlist.cover}
                        alt={playlist.name}
                        fill
                        className="object-cover"
                        sizes="160px"
                      />
                    ) : (
                      <DiscAlbum className="text-muted-foreground absolute inset-0 m-auto size-8" />
                    )}
                  </div>
                  <p className="mt-2 truncate text-sm font-medium">{playlist.name}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      />
    </div>
  );
}
